import { gql } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { useTranslation } from 'next-i18next'
import { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import BaseButton from 'src/components/Buttons/BaseButton'
import PhoneField from 'src/components/Inputs/PhoneField'
import useLogger from 'src/context/LoggerProvider/useLogger'
import {
  IMutation,
  IMutationSendCustomerInfoEmailArgs,
  Interest,
  LocationType
} from 'src/types/graphql'
import { verifyBotId } from 'src/utils/botid'
import ga from 'src/utils/pixels/gtag'

/**
 * Build B — notify-me capture on the UnavailableBanner (paused / sold-out
 * listing). Turns the SEO dead-end into a demand signal: a phone-only submit
 * creates a HubSpot demand lead + a DemandWatch keyed to THIS listing's geo
 * (spec: spotme-server/docs/recovery-nurture-personalization.md), so the
 * existing supply-arrival re-touch reaches the renter when matching supply
 * appears. Reuses `sendCustomerInfoEmail` with `leadSource: 'unavailable_listing'`
 * and the listing's coords as `searchInput.center` (the demand-watch geo). The
 * host is deliberately NOT notified (handled server-side).
 */
const SEND_CUSTOMER_INFO_EMAIL = gql`
  mutation SendCustomerInfoEmail(
    $customerInfo: CustomerInfoInput!
    $searchInput: SearchLocationsInput
  ) {
    sendCustomerInfoEmail(
      customerInfo: $customerInfo
      searchInput: $searchInput
    )
  }
`

/** Cap the BotID pre-flight so a slow edge check never blocks the submit. */
const BOTID_SUBMIT_WAIT_MS = 2500

type FormValues = { phone: string }

interface NotifyMeFormProps {
  locationId: string
  configId: string
  locationType?: LocationType | null
  city?: string | null
  coordinates?: { lat: number; lng: number } | null
}

const NotifyMeForm = ({
  locationId,
  configId,
  locationType,
  city,
  coordinates
}: NotifyMeFormProps) => {
  const { t } = useTranslation('details')
  const logger = useLogger()
  const [submitted, setSubmitted] = useState(false)
  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: { phone: '' },
    mode: 'onTouched'
  })
  const [send, { loading }] = useMutation<
    Pick<IMutation, 'sendCustomerInfoEmail'>,
    IMutationSendCustomerInfoEmailArgs
  >(SEND_CUSTOMER_INFO_EMAIL)

  const onSubmit = useCallback(
    async ({ phone }: FormValues) => {
      const vertical =
        locationType === LocationType.Parking ? 'parking' : 'storage'
      try {
        // Soft-flag bot defense — never throws; races a timeout so a slow
        // edge check falls through unflagged (fail-open).
        const verdict = await Promise.race([
          verifyBotId({ surface: 'unavailable_notify' }),
          new Promise<undefined>((resolve) =>
            setTimeout(() => resolve(undefined), BOTID_SUBMIT_WAIT_MS)
          )
        ])
        await send({
          variables: {
            customerInfo: {
              phone,
              // CustomerInfoInput requires email + name; empty strings satisfy
              // the schema and the resolver maps '' → undefined (no CRM clobber).
              email: '',
              name: '',
              interest: Interest.Rent,
              leadSource: 'unavailable_listing',
              locationId,
              configId,
              locationType: locationType ?? undefined,
              nearestCity: city ?? undefined,
              botIdFlagged: verdict?.isBot === true ? true : undefined
            },
            searchInput: coordinates
              ? { center: { lat: coordinates.lat, lng: coordinates.lng } }
              : undefined
          }
        })
        ga.event('notify_me_submit', { listing_id: locationId, vertical })
        setSubmitted(true)
      } catch (error) {
        // Best-effort capture — a dead-end page should never surface an error.
        // The record is fire-and-forget server-side anyway; show the confirm.
        logger.error('Notify-me submit failed', { error, locationId })
        setSubmitted(true)
      }
    },
    [send, locationId, configId, locationType, city, coordinates, logger]
  )

  if (submitted) {
    return (
      <p className="mt-5 text-[14px] leading-relaxed text-ink-700 md:mt-6 md:text-[15px]">
        {city
          ? t('UNAVAILABLE.NOTIFY.SUCCESS_WITH_CITY', { city })
          : t('UNAVAILABLE.NOTIFY.SUCCESS')}
      </p>
    )
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="mt-5 border-t border-ink-100/15 pt-5 md:mt-6 md:pt-6"
    >
      <p className="text-[14px] leading-relaxed text-ink-700 md:text-[15px]">
        {t('UNAVAILABLE.NOTIFY.PROMPT')}
      </p>
      <div className="mt-3 flex flex-col gap-2 md:flex-row md:items-start md:gap-3">
        <div className="md:flex-1">
          <PhoneField<FormValues>
            name="phone"
            control={control}
            rules={{ required: true }}
            hideHelper
            placeholder={t('UNAVAILABLE.NOTIFY.PHONE_PLACEHOLDER')}
          />
        </div>
        <BaseButton type="submit" loading={loading} className="md:shrink-0">
          {t('UNAVAILABLE.NOTIFY.CTA')}
        </BaseButton>
      </div>
    </form>
  )
}

export default NotifyMeForm
